<template>
  <div class="content">
    <div class="info-panel">
      <div class="left">
        <div>
          <input
            name="title"
            type="text"
            placeholder="Title"
            v-model="currentBooking.title"
            :disabled="pinStatus !== 'unlocked'"/>
          <textarea
            name="description"
            type="text"
            placeholder="Your Description"
            v-model="currentBooking.description"
            :disabled="pinStatus !== 'unlocked'" />
        </div>
        <div>
          <div class="time-label">
            <div><label>Time</label></div>
            <div><label><b>{{ selectedTime }}</b></label></div>
          </div>
          <SlideTimePicker
            v-model="currentTime"
            :start="availabilityStart"
            :end="availabilityEnd"
            :disabled="pinStatus !== 'unlocked'"
          />
        </div>
        <div v-if="user" class="organizer"><label>Organizer: {{ user.email }}</label></div>
        <div class="action-buttons">
          <div :class='`pin-entry-container ${pinStatus}`'>
            <PinEntry @statusChange="handlePinStatusChange" @success="handlePinSuccess" />
          </div>
        </div>
      </div>
      <div class="right">
        <div><h3>Invitees</h3></div>
        <div>
          <Invites v-model="currentBooking.invites_list" :values="currentBooking.invites_list" />
        </div>
        <div class="action-buttons">
          <div class="save-button-container">
            <SaveButton v-if="pinStatus === 'unlocked'" @click="handleSaveClick" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';

import Invites from '../Invites';
import PinEntry from '../PinEntry';
import SlideTimePicker from '../SlideTimePicker';
import SaveButton from '../ActionButtons/Save';
import PermissionMixins from '../../../mixins/permission-mixins';
import BookingMixins from '../../../mixins/booking-mixins';
import { saveBooking, updateBooking } from '../../../services/endpoints';

const getMinutes = time => parseInt(time.split(':')[1], 10);
const getHour = time => parseInt(time.split(':')[0], 10);

export default {
  mixins: [PermissionMixins, BookingMixins],

  created() {
    this.currentBooking = Object.assign(this.currentBooking, this.booking);

    if (this.id) {
      // Deep copy the model so we don't mutate the state by accident.
      this.currentBooking = JSON.parse(JSON.stringify(this.getBookingById(this.bookings, this.id)));
      const from = moment(this.currentBooking.start_date).format('HH:mm');
      const to = moment(this.currentBooking.end_date).format('HH:mm');
      this.currentTime = [from, to];
    } else {
      this.currentTime = [moment(this.startDate).format('HH:mm'), moment(this.startDate).add(30, 'minutes').format('HH:mm')];
    }
  },

  components: { PinEntry, SlideTimePicker, SaveButton, Invites },

  data: () => ({
    pinStatus: 'locked',
    user: null,
    currentTime: [],
    currentBooking: {
      invites_list: [],
    },
  }),

  props: {
    id: Number,
    time: Array,
    startDate: [String, Date, Object],
    endDate: [String, Date, Object],
  },

  computed: {
    ...mapState({
      pin: state => state.auth.pin,
      bookings: state => state.bookings,
      selectedDate: state => state.selectedDate,
      spaceAvailabilityStart: state => state.space.availiblity_start,
      spaceAvailabilityEnd: state => state.space.availiblity_end,
    }),

    availabilityStart() {
      if (!this.id && this.startDate && this.endDate) {
        return moment(this.startDate).format('HH:mm');
      }

      return this.getDateTime(this.spaceAvailabilityStart || '00:00', 'HH:mm');
    },

    availabilityEnd() {
      if (!this.id && this.startDate && this.endDate) {
        return moment(this.endDate).format('HH:mm');
      }

      return this.getDateTime(this.spaceAvailabilityEnd || '23:30', 'HH:mm');
    },

    selectedTime() {
      return `${this.getDateTime(this.currentTime[0], 'h:mma')} - ${this.getDateTime(this.currentTime[1], 'h:mma')}`;
    },
  },

  methods: {
    ...mapActions(['refreshBookings', 'setSuccessMessage', 'refreshBookings', 'updateBooking']),

    handlePinStatusChange(status) {
      this.pinStatus = status;

      if (status !== 'unlocked') {
        this.user = null;
        this.auth.user_role = null;
      }
    },

    handlePinSuccess({ result }) {
      this.user = result;
      this.auth.user_role = result.user_role;
    },

    handleSaveClick() {
      const data = {
        title: this.currentBooking.title,
        description: this.currentBooking.description,
        pin: this.pin,
        start_date: this.getDateTime(this.currentTime[0]),
        end_date: this.getDateTime(this.currentTime[1]),
        invites_list: JSON.stringify(this.currentBooking.invites_list),
      };

      let endpoint = () => saveBooking(data);
      if (this.id) {
        endpoint = () =>
          new Promise((resolve, reject) => {
            updateBooking(data, this.id).then(() => {
              this.updateBooking({ data, bookingId: this.id });
              resolve();
            }).catch(reject);
          });
      }

      endpoint().then(() => {
        this.setSuccessMessage('Booking saved');
        this.refreshBookings();
        this.$emit('close');
      });
    },

    getDateTime(timeString, format = 'YYYY-MM-DD HH:mm:00') {
      const hour = getHour(timeString);
      const minute = getMinutes(timeString);
      const selectedDate = moment(this.selectedDate);
      return selectedDate.utc().set({ hour, minute }).format(format);
    },
  },
};
</script>


<style lang="scss" scoped>
  h3 {
    opacity: 0.9;
    color: #363636;
    font-size: 25px;
    letter-spacing: -0.21px;
    line-height: 30px;
    margin-bottom: 40px;
  }
  .content {
    overflow: visible;

    .info-panel {
      display: flex;
      border-radius: 24px;
      background-color: #F7F7F7;
      justify-content: space-between;
    }

    .left, .right {
      padding: 30px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 90vh;
    }

    .left {
      flex: 1;

      .action-buttons {
        position: relative;
        height: 75px;
        display: flex;

        .pin-entry-container {
          position: absolute;
          left: -65px;
          flex: 0;
        }
      }

      .organizer {
        font-size: 22px;
        letter-spacing: -0.19px;
        line-height: 27px;
      }

      .time-label {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
      }
    }

    .right {
      width: 30vw;
      border-radius: 24px;
      background-color: #FFFFFF;

      > div {
        flex: 0;
      }

      .action-buttons {
        position: relative;
        height: 75px;
        display: flex;
        flex: 1;

        .save-button-container {
          position: absolute;
          right: -50px;
          bottom: 0;
        }
      }
    }
  }

  input, textarea {
    background: none;
    border: none;
    border-bottom: 1px solid #D8D8D8;
    outline: none;
    width: 100%;
  }

  input[name="title"] {
    height: 68px;
    color: #11141F;
    font-size: 56px;
    font-weight: bold;
    letter-spacing: -0.47px;
    line-height: 68px;
  }

  textarea[name="description"] {
    color: #11141F;
    font-size: 22px;
    letter-spacing: -0.19px;
    line-height: 27px;
    min-height: 47px;
    max-height: 247px;
    margin-top: 20px;
  }

  label {
    height: 27px;
    width: 46px;
    opacity: 0.9;
    color: #11141F;
    font-size: 22px;
    letter-spacing: -0.19px;
    line-height: 27px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Booking/Modal/BookingForm.vue